import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Wand2, Send, Loader2, Trash2, Sparkles, MessageSquare, Paperclip, X, Image as ImageIcon, Video as VideoIcon } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const QUICK_EXAMPLES = [
  'תראה סטטוס אינטגרציות',
  'כמה הזמנות יש לי ב-DB',
  'שורות 100-150 של /app/backend/server.py',
  'רשימת קבצים ב-/app/backend (ls -la)',
  'תראה לוג 30 שורות אחרונות',
  'הזמנות של היום',
  'לקוחות מובילים',
  'שנה צבע ראשי לכחול',
  'באתר הציבורי הוסף צל לכרטיסים',
  'הצג שינויים / בטל הכל',
];

export default function SiteEditorChat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [overrides, setOverrides] = useState(null);
  const [attachments, setAttachments] = useState([]); // [{file, preview, type}]
  const fileInputRef = useRef(null);
  const scrollRef = useRef(null);

  const loadHistory = async () => {
    try {
      const [h, o] = await Promise.all([
        axios.get(`${API}/site-editor/history`),
        axios.get(`${API}/site-overrides`),
      ]);
      setMessages(h.data?.messages || []);
      setOverrides(o.data);
    } catch { /* ignore */ }
  };

  useEffect(() => { loadHistory(); }, []);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, sending]);

  const refreshSite = () => {
    // Ping the SiteOverridesProvider by dispatching a reload of overrides
    // (provider polls every 30s but we want instant feedback)
    window.dispatchEvent(new Event('site-overrides-refresh'));
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    const hasFiles = attachments.length > 0;
    if ((!msg && !hasFiles) || sending) return;
    setSending(true);
    const userContent = msg || (hasFiles ? '(קובץ/ים מצורפים)' : '');
    setMessages(m => [...m, {
      role: 'user',
      content: userContent,
      attachments: attachments.map(a => ({ preview: a.preview, type: a.type, name: a.file.name })),
      at: new Date().toISOString(),
    }]);
    setInput('');
    const currentAttachments = attachments;
    setAttachments([]);
    try {
      let r;
      if (hasFiles) {
        const fd = new FormData();
        fd.append('message', msg || 'תסתכל על הקבצים המצורפים והצע לי מה לעשות');
        currentAttachments.forEach(a => fd.append('files', a.file));
        r = await axios.post(`${API}/site-editor/message-with-files`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        r = await axios.post(`${API}/site-editor/message`, { message: msg });
      }
      setMessages(m => [...m, { role: 'assistant', content: r.data.response || '(אין תגובה)', at: new Date().toISOString() }]);
      const o = await axios.get(`${API}/site-overrides`);
      setOverrides(o.data);
      refreshSite();
      if (r.data?.skipped > 0) {
        toast.warning(`${r.data.skipped} קבצים לא נטענו (פורמט לא נתמך או גדול מ-10MB)`);
      }
    } catch (e) {
      toast.error('שגיאה בשליחה');
      setMessages(m => [...m, { role: 'assistant', content: `שגיאה: ${e?.response?.data?.detail || e.message}`, at: new Date().toISOString() }]);
    }
    setSending(false);
  };

  const onFilesSelected = (e) => {
    const files = Array.from(e.target.files || []);
    const valid = [];
    for (const f of files) {
      const isImage = f.type.startsWith('image/');
      const isVideo = f.type.startsWith('video/');
      if (!isImage && !isVideo) {
        toast.error(`${f.name}: פורמט לא נתמך (רק תמונות וסרטונים)`);
        continue;
      }
      if (f.size > 10 * 1024 * 1024) {
        toast.error(`${f.name}: גדול מ-10MB`);
        continue;
      }
      valid.push({
        file: f,
        preview: URL.createObjectURL(f),
        type: isVideo ? 'video' : 'image',
      });
    }
    setAttachments(prev => [...prev, ...valid].slice(0, 4));
    e.target.value = '';
  };

  const removeAttachment = (idx) => {
    setAttachments(prev => {
      const arr = [...prev];
      try { URL.revokeObjectURL(arr[idx].preview); } catch {}
      arr.splice(idx, 1);
      return arr;
    });
  };

  // Paste handler - capture Ctrl+V for images
  const onPaste = (e) => {
    const items = e.clipboardData?.items || [];
    const imgFiles = [];
    for (const it of items) {
      if (it.kind === 'file') {
        const f = it.getAsFile();
        if (f && (f.type.startsWith('image/') || f.type.startsWith('video/'))) {
          imgFiles.push({
            file: new File([f], `paste-${Date.now()}.${(f.type.split('/')[1] || 'png')}`, { type: f.type }),
            preview: URL.createObjectURL(f),
            type: f.type.startsWith('video/') ? 'video' : 'image',
          });
        }
      }
    }
    if (imgFiles.length > 0) {
      e.preventDefault();
      setAttachments(prev => [...prev, ...imgFiles].slice(0, 4));
      toast.success(`${imgFiles.length} קובץ/ים הודבק`);
    }
  };

  // Global paste listener (works even when chat input is not focused)
  useEffect(() => {
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearChat = async () => {
    if (!window.confirm('למחוק היסטוריית שיחה?')) return;
    await axios.delete(`${API}/site-editor/history`);
    setMessages([]);
    toast.success('היסטוריה נמחקה');
  };

  const revertAll = async () => {
    if (!window.confirm('לבטל את כל השינויים ולחזור לברירת מחדל?')) return;
    try {
      await axios.delete(`${API}/site-editor/overrides`);
      toast.success('כל השינויים בוטלו');
      const o = await axios.get(`${API}/site-overrides`);
      setOverrides(o.data);
      refreshSite();
      // Reload the page to clear all applied overrides
      setTimeout(() => window.location.reload(), 800);
    } catch { toast.error('שגיאה'); }
  };

  const activeCount = overrides
    ? (overrides.text_replaces?.length || 0) + Object.keys(overrides.colors || {}).length + Object.keys(overrides.texts || {}).length + (overrides.hides?.length || 0)
    : 0;

  return (
    <div className="space-y-4 pb-6" data-testid="site-editor-chat">
      {/* Header */}
      <div className="bg-gradient-to-l from-purple-700 via-fuchsia-700 to-pink-700 text-white rounded-2xl p-5 shadow-lg">
        <div className="flex items-center gap-3">
          <div className="bg-white/15 p-2.5 rounded-xl"><Wand2 className="w-7 h-7" /></div>
          <div>
            <h1 className="text-2xl font-bold">צ'אט שינויים</h1>
            <p className="text-purple-100 text-sm">דבר איתי בעברית חופשית - אני אבצע שינויים באתר שלך בזמן אמת 🎨</p>
          </div>
        </div>
      </div>

      {/* Active overrides banner */}
      <div className={`rounded-2xl p-3 border flex items-center justify-between ${activeCount > 0 ? 'bg-fuchsia-50 border-fuchsia-300' : 'bg-stone-50 border-stone-200'}`}>
        <div className="flex items-center gap-2 text-sm">
          <Sparkles className={`w-4 h-4 ${activeCount > 0 ? 'text-fuchsia-600' : 'text-stone-400'}`} />
          <span className="font-bold">
            {activeCount > 0 ? `${activeCount} שינויים פעילים באתר` : 'אין שינויים פעילים - האתר בברירת מחדל'}
          </span>
        </div>
        {activeCount > 0 && (
          <button
            onClick={revertAll}
            className="text-xs bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 px-3 py-1.5 rounded-lg flex items-center gap-1 font-bold"
            data-testid="revert-all-btn"
          >
            <Trash2 className="w-3.5 h-3.5" /> אפס הכל
          </button>
        )}
      </div>

      {/* Quick examples */}
      <div className="bg-white rounded-2xl p-4 shadow border border-stone-200">
        <div className="text-sm text-stone-600 mb-2 font-semibold">💡 דוגמאות (לחץ כדי לשלוח):</div>
        <div className="flex flex-wrap gap-2">
          {QUICK_EXAMPLES.map((ex, i) => (
            <button
              key={i}
              onClick={() => send(ex)}
              disabled={sending}
              className="bg-stone-100 hover:bg-fuchsia-50 hover:border-fuchsia-400 border border-stone-300 text-stone-800 text-xs px-3 py-1.5 rounded-full transition disabled:opacity-50"
              data-testid={`editor-example-${i}`}
            >
              {ex}
            </button>
          ))}
        </div>
        <div className="mt-3 flex items-start gap-2 text-xs text-stone-600 bg-fuchsia-50 border border-fuchsia-200 rounded-lg p-2">
          <ImageIcon className="w-4 h-4 text-fuchsia-600 shrink-0 mt-0.5" />
          <div>
            <b>חדש!</b> אפשר לצרף תמונות/סרטונים מהגלריה או <b>להדביק עם Ctrl+V</b> (עתק/הדבק צילומי מסך!). בנוסף אני יכול לנהל הזמנות, מוצרים, לקוחות וקופונים ישירות דרך הצ'אט.
          </div>
        </div>
      </div>

      {/* Chat */}
      <div className="bg-white rounded-2xl shadow border border-stone-200 flex flex-col" style={{ height: '60vh' }}>
        <div className="flex items-center justify-between p-3 border-b border-stone-200">
          <div className="flex items-center gap-2 font-bold text-stone-700"><MessageSquare className="w-4 h-4" /> שיחה</div>
          <button
            onClick={clearChat}
            className="text-red-600 hover:bg-red-50 p-1.5 rounded-lg flex items-center gap-1 text-xs"
            data-testid="clear-editor-chat-btn"
          >
            <Trash2 className="w-3.5 h-3.5" /> נקה
          </button>
        </div>

        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto p-4 space-y-2 bg-stone-50"
          style={{ overscrollBehavior: 'contain' }}
          data-testid="editor-messages"
        >
          {messages.length === 0 && (
            <div className="text-center text-stone-400 mt-10">
              <Wand2 className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <div className="text-sm">שלח פקודה בעברית ואני אבצע אותה באתר</div>
              <div className="text-xs mt-1 text-stone-500">השינויים חלים מיידית - ללא Deploy! 🚀</div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-2 whitespace-pre-wrap text-sm shadow-sm ${
                  m.role === 'user' ? 'bg-fuchsia-600 text-white rounded-bl-md' : 'bg-white border border-stone-200 text-stone-800 rounded-br-md'
                }`}
                data-testid={`editor-msg-${m.role}-${i}`}
              >
                {m.attachments && m.attachments.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-1.5">
                    {m.attachments.map((a, j) => (
                      <div key={j} className="relative">
                        {a.type === 'video' ? (
                          <video src={a.preview} className="w-20 h-20 object-cover rounded-lg border border-white/30" muted />
                        ) : (
                          <img src={a.preview} alt="attached" className="w-20 h-20 object-cover rounded-lg border border-white/30" />
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {m.content}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-white border border-stone-200 rounded-2xl px-4 py-2 flex items-center gap-2 text-stone-500 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" /> מבצע...
              </div>
            </div>
          )}
        </div>

        {/* Attachment previews */}
        {attachments.length > 0 && (
          <div className="px-3 pt-3 flex flex-wrap gap-2 border-t border-stone-200 bg-fuchsia-50/40" data-testid="attachments-preview">
            {attachments.map((a, i) => (
              <div key={i} className="relative group">
                {a.type === 'video' ? (
                  <div className="relative w-20 h-20 rounded-lg overflow-hidden border-2 border-fuchsia-400 bg-black">
                    <video src={a.preview} className="w-full h-full object-cover" muted />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <VideoIcon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                ) : (
                  <img src={a.preview} alt="" className="w-20 h-20 object-cover rounded-lg border-2 border-fuchsia-400" />
                )}
                <button
                  onClick={() => removeAttachment(i)}
                  className="absolute -top-1.5 -right-1.5 bg-red-500 hover:bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center shadow"
                  data-testid={`remove-attachment-${i}`}
                  type="button"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="p-3 border-t border-stone-200 flex gap-2 items-center">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            className="hidden"
            onChange={onFilesSelected}
            data-testid="editor-file-input"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={sending || attachments.length >= 4}
            className="p-2.5 rounded-xl border border-stone-300 hover:bg-fuchsia-50 hover:border-fuchsia-400 disabled:opacity-50"
            title="הוסף תמונה או סרטון"
            data-testid="editor-attach-btn"
          >
            <Paperclip className="w-5 h-5 text-stone-700" />
          </button>
          <input
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder='שלח הודעה או צרף תמונה / סרטון'
            disabled={sending}
            className="flex-1 border border-stone-300 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-fuchsia-500 text-right"
            data-testid="editor-input"
          />
          <button
            onClick={() => send()}
            disabled={sending || (!input.trim() && attachments.length === 0)}
            className="bg-fuchsia-600 hover:bg-fuchsia-700 disabled:bg-stone-300 text-white px-5 py-2.5 rounded-xl flex items-center gap-2 font-semibold"
            data-testid="editor-send-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            שלח
          </button>
        </div>
      </div>
    </div>
  );
}
